import { makeStyles } from '@material-ui/styles'
import BaseModal from 'components/common/modals/BaseModal'
import React, { useCallback, useEffect, useState } from 'react'
import { trackEvent } from 'utils/analyticsUtils'
import useZoomMeeting from './useZoomMeeting'
import { logError } from 'utils/logUtils'
import Actions from 'actions'
import { prettyError } from '@grin-rnd/grin-api-sdk/dist/utils/ErrorUtils'
import PatientContextView from './PatientContextView/PatientContextView'
import { mapToCreateNoteInput } from 'utils/mappers/noteMappers'
import { useCreateNoteMutation } from 'store/modules/PatientNotes'
import useShowSnackbar from 'hooks/useShowSnackbar'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import VideoEndedMessage from './VideoEndedMessage'
import ConfirmationModal from 'components/common/ConfirmationModal'
import { LiveCallTypes } from './liveCallConsts'
import useSendChatMessage from 'hooks/useSendChatMessage'
import SendInvitationTooltip from './PatientContextView/SendInvitationTooltip'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '98%',
    margin: 5,
    padding: 12
  },
  dialogContent: {
    padding: '30px 24px 24px 24px!important'
  },
  meetingSdkElement: {
    width: '100%',
    height: '100%'
  },
  contentWrapper: {
    display: 'flex',
    justifyContent: 'space-between'
  }
}))

const LiveCallModal = ({ isOpen, setIsOpen, isInstantMeeting = true }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { sendMessage } = useSendChatMessage()
  const { patient } = useSelector(state => state.patientsReducer)

  const [note, setNote] = useState('')
  const [isInitiated, setIsInitiated] = useState(false)
  const [meetingNumber, setMeetingNumber] = useState()
  const [shouldDisplayLeaveMeetingConfirmation, setShouldDisplayLeaveMeetingConfirmation] = useState(false)
  const [wasPatientInvited, setWasPatientInvited] = useState(false)

  const { meetingSdkElement, zoomClient, initZoom, createMeeting, meetingInfo } = useZoomMeeting({})

  const [createNote, { isError: isFailedCreatingNote }] = useCreateNoteMutation()
  useShowSnackbar({
    isOpen: isFailedCreatingNote,
    message: t('pages.patients.patientSwitchableView.notes.createNoteError')
  })

  const handleCloseAttempt = useCallback(() => {
    trackEvent('Live call modal - Close attempt')
    setShouldDisplayLeaveMeetingConfirmation(true)
  }, [])

  const handleCancelCloseAttempt = useCallback(() => {
    trackEvent('Live call modal - Close attempt cancelled')
    setShouldDisplayLeaveMeetingConfirmation(false)
  }, [])

  const handleClose = useCallback(async () => {
    trackEvent('Live call modal - Closed')
    try {
      await zoomClient.endMeeting()
    } catch (ex) {
      logError('An error occured while trying to end meeting on closing modal (this not necessarily an issue', {
        error: prettyError(ex)
      })
    }
    setIsOpen(false)

    if (!!note.trim()) {
      const payload = mapToCreateNoteInput({ noteContent: note.trim(), additionalMetadata: { noteType: 'liveCall' } })
      await createNote({ variables: payload })
    }

    setNote('')
  }, [createNote, note, setIsOpen, zoomClient])

  const handleCreateMeeting = useCallback(async () => {
    try {
      const parentWidth = meetingSdkElement.parentElement.clientWidth
      const parentHeight = meetingSdkElement.parentElement.clientHeight

      const zoomWidth = parentWidth
      const zoomHeight = parentHeight * 0.9

      await initZoom({ zoomWidth, zoomHeight })

      const meetingNumber = await createMeeting()

      if (!meetingNumber) {
        throw new Error('Failed creating meeting')
      }

      setMeetingNumber(meetingNumber)
    } catch (ex) {
      logError('An error occured while trying to initiate zoom meeting', {
        error: prettyError(ex)
      })
      dispatch(
        Actions.showSnackbar({
          type: 'error',
          text: t('pages.patients.patientSidebar.items.liveCalls.createMeetingError')
        })
      )
    }
  }, [meetingSdkElement, initZoom, createMeeting, dispatch, t])

  const handleSendInvitationToPatientClicked = useCallback(() => {
    trackEvent('Live call modal - Send invitation to patient clicked', {
      patientId: patient.id
    })
    setWasPatientInvited(true)
    sendMessage({
      type: LiveCallTypes.InstantLiveCall,
      additionalFields: { meetingNumber }
    })
  }, [meetingNumber, patient.id, sendMessage, setWasPatientInvited])

  useEffect(() => {
    const initiateMeeting = async () => {
      if (isInstantMeeting && meetingSdkElement && !isInitiated) {
        setIsInitiated(true)
        await handleCreateMeeting()
      }
    }

    initiateMeeting()
  }, [handleCreateMeeting, isInitiated, isInstantMeeting, meetingSdkElement])

  return (
    isOpen && (
      <BaseModal
        open={isOpen}
        handleClose={handleCloseAttempt}
        className={classes.root}
        contentClassName={classes.dialogContent}
        contentWrapperClassName={classes.contentWrapper}
        disableEnforceFocus
      >
        {meetingInfo.isEnded && <VideoEndedMessage onCloseClicked={handleClose} />}
        <div style={{ position: 'relative', width: '60%' }}>
          <div id="meetingSDKElement" className={classes.meetingSdkElement}></div>
          <SendInvitationTooltip
            isOpen={meetingNumber && !meetingInfo.isEnded && (!wasPatientInvited || !meetingInfo.hasPatientJoined)}
            wasPatientInvited={wasPatientInvited}
            onSendClicked={handleSendInvitationToPatientClicked}
          />
        </div>
        <PatientContextView note={note} setNote={setNote} meetingNumber={meetingNumber} />
        {shouldDisplayLeaveMeetingConfirmation && (
          <ConfirmationModal
            largerButtons
            isOpen={shouldDisplayLeaveMeetingConfirmation}
            title={t('pages.patients.patientSidebar.items.liveCalls.leaveMeetingConfirmationModal.title')}
            text={t('pages.patients.patientSidebar.items.liveCalls.leaveMeetingConfirmationModal.description')}
            acceptButtonText={t('pages.patients.patientSidebar.items.liveCalls.leaveMeetingConfirmationModal.leave')}
            declineButtonText={t('general.cancel')}
            onConfirm={handleClose}
            onClose={handleCancelCloseAttempt}
          />
        )}
      </BaseModal>
    )
  )
}

export default LiveCallModal
